import React, { useState, useEffect } from 'react';
import { ChevronRight, Plus, Trash2, Upload, Download, FileText, Settings } from 'lucide-react';

const FinancialApp = () => {
  const [view, setView] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [trialBalance, setTrialBalance] = useState([]);
  const [mappings, setMappings] = useState({});
  const [companyName, setCompanyName] = useState('');
  const [periodEnding, setPeriodEnding] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('prettyFinancials');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setTemplates(data.templates || []);
        setMappings(data.mappings || {});
      } catch (e) {
        console.error('Error loading saved data:', e);
      }
    }
  }, []);

  const saveToStorage = (newTemplates, newMappings) => {
    localStorage.setItem('prettyFinancials', JSON.stringify({
      templates: newTemplates,
      mappings: newMappings
    }));
  };

  const createNewTemplate = () => {
    const newTemplate = {
      id: Date.now().toString(),
      name: 'New Template',
      type: 'income_statement',
      items: []
    };
    const updated = [...templates, newTemplate];
    setTemplates(updated);
    setActiveTemplate(newTemplate);
    saveToStorage(updated, mappings);
  };

  const updateTemplate = (updatedTemplate) => {
    const updated = templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t);
    setTemplates(updated);
    setActiveTemplate(updatedTemplate);
    saveToStorage(updated, mappings);
  };

  const deleteTemplate = (id) => {
    const updated = templates.filter(t => t.id !== id);
    setTemplates(updated);
    if (activeTemplate?.id === id) setActiveTemplate(null);
    saveToStorage(updated, mappings);
  };

  const handleTrialBalanceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      let lines = text.split('\n').map(line => line.split(','));
      if (lines[0].length === 1) {
        lines = text.split('\n').map(line => line.split('\t'));
      }
      
      const dataRows = lines.slice(1).filter(row => {
        const firstCol = row[0]?.trim();
        const amountCol = row[row.length - 1]?.trim();
        if (!firstCol || firstCol.length === 0) return false;
        if (firstCol === firstCol.toUpperCase() && !/^\d/.test(firstCol)) return false;
        if (firstCol.toUpperCase().startsWith('TOTAL')) return false;
        const amount = parseFloat(amountCol?.replace(/[,$]/g, '')) || 0;
        if (amount === 0) return false;
        if (!/^\d/.test(firstCol)) return false;
        return true;
      });

      const parsed = dataRows.map((row, index) => {
        if (row.length < 2) return null;
        const fullAccount = row[0]?.trim();
        const amount = parseFloat(row[row.length - 1]?.replace(/[,$]/g, '')) || 0;
        const match = fullAccount.match(/^(\d{5})\s*-\s*(.+)/);
        let accountNumber, accountName;
        if (match) {
          accountNumber = match[1];
          accountName = fullAccount;
        } else {
          accountNumber = (index + 1).toString();
          accountName = fullAccount;
        }
        return { accountNumber, accountName, amount };
      }).filter(item => item && item.accountName && item.amount !== 0);

      if (parsed.length === 0) {
        alert('No valid data found in file.');
        return;
      }

      const lineItems = activeTemplate.items.filter(i => i.type === 'line');
      const newMappings = { ...mappings };
      let autoMappedCount = 0;

      parsed.forEach(account => {
        let matchedItem = lineItems.find(item => 
          item.name.toLowerCase() === account.accountName.toLowerCase()
        );
        if (!matchedItem) {
          matchedItem = lineItems.find(item => 
            account.accountName.toLowerCase().includes(item.name.toLowerCase())
          );
        }
        if (matchedItem) {
          newMappings[account.accountNumber] = matchedItem.id;
          autoMappedCount++;
        }
      });

      setMappings(newMappings);
      saveToStorage(templates, newMappings);
      setTrialBalance(parsed);
      setView('mapping');

      if (autoMappedCount > 0) {
        alert(`Uploaded ${parsed.length} accounts. Auto-mapped ${autoMappedCount}.`);
      }
    } catch (error) {
      alert('Error parsing file.');
      console.error(error);
    }
  };

  const updateMapping = (accountNumber, lineItemId) => {
    const updated = { ...mappings, [accountNumber]: lineItemId };
    setMappings(updated);
    saveToStorage(templates, updated);
  };

  const unmappedAccounts = trialBalance.filter(acc => !mappings[acc.accountNumber]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-6 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Pretty Financials</h1>
          <p className="text-slate-300">Professional Financial Statement Builder</p>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-8">
            <button onClick={() => setView('templates')} className={`py-4 px-2 border-b-2 font-medium text-sm ${view === 'templates' ? 'border-blue-900 text-blue-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <Settings className="inline w-4 h-4 mr-2" />
              Templates
            </button>
            <button onClick={() => setView('mapping')} disabled={!activeTemplate} className={`py-4 px-2 border-b-2 font-medium text-sm ${view === 'mapping' ? 'border-blue-900 text-blue-900' : 'border-transparent text-gray-500'}`}>
              <Upload className="inline w-4 h-4 mr-2" />
              Mapping
            </button>
            <button onClick={() => setView('statements')} disabled={!activeTemplate || trialBalance.length === 0} className={`py-4 px-2 border-b-2 font-medium text-sm ${view === 'statements' ? 'border-blue-900 text-blue-900' : 'border-transparent text-gray-500'}`}>
              <FileText className="inline w-4 h-4 mr-2" />
              Statements
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {view === 'templates' && (
          <TemplateBuilder
            templates={templates}
            activeTemplate={activeTemplate}
            setActiveTemplate={setActiveTemplate}
            createNewTemplate={createNewTemplate}
            updateTemplate={updateTemplate}
            deleteTemplate={deleteTemplate}
          />
        )}
        
        {view === 'mapping' && (
          <MappingInterface
            activeTemplate={activeTemplate}
            trialBalance={trialBalance}
            mappings={mappings}
            updateMapping={updateMapping}
            handleFileUpload={handleTrialBalanceUpload}
            unmappedAccounts={unmappedAccounts}
            generateStatements={() => setView('statements')}
          />
        )}
        
        {view === 'statements' && (
          <StatementView
            activeTemplate={activeTemplate}
            trialBalance={trialBalance}
            mappings={mappings}
            companyName={companyName}
            setCompanyName={setCompanyName}
            periodEnding={periodEnding}
            setPeriodEnding={setPeriodEnding}
          />
        )}
      </div>
    </div>
  );
};

const TemplateBuilder = ({ templates, activeTemplate, setActiveTemplate, createNewTemplate, updateTemplate, deleteTemplate }) => {
  const [editingItem, setEditingItem] = useState(null);
  const [showCSVUpload, setShowCSVUpload] = useState(false);

  const addItem = (type) => {
    if (!activeTemplate) return;
    const newItem = {
      id: Date.now().toString(),
      type,
      name: type === 'header' ? 'New Header' : type === 'line' ? 'New Line Item' : 'New Calculation',
      parentId: type === 'line' ? null : undefined,
      calculation: type === 'calculation' ? { operator: 'subtract', lineIds: [] } : null
    };
    updateTemplate({ ...activeTemplate, items: [...activeTemplate.items, newItem] });
  };

  const updateItem = (itemId, updates) => {
    updateTemplate({
      ...activeTemplate,
      items: activeTemplate.items.map(item => item.id === itemId ? { ...item, ...updates } : item)
    });
  };

  const deleteItem = (itemId) => {
    updateTemplate({ ...activeTemplate, items: activeTemplate.items.filter(item => item.id !== itemId) });
  };

  const moveItem = (itemId, direction) => {
    const items = [...activeTemplate.items];
    const index = items.findIndex(i => i.id === itemId);
    if (direction === 'up' && index > 0) {
      [items[index], items[index - 1]] = [items[index - 1], items[index]];
    } else if (direction === 'down' && index < items.length - 1) {
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
    }
    updateTemplate({ ...activeTemplate, items });
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const lines = text.split('\n').map(line => line.split(',').map(cell => cell.trim().replace(/^"|"$/g, '')));
      const newItems = [];
      const headerMap = {};

      lines.slice(1).forEach((row, idx) => {
        if (!row[0] || !row[1]) return;
        const type = row[0].toLowerCase();
        const name = row[1];
        const parent = row[2];
        const item = { id: `csv_${Date.now()}_${idx}`, type, name };

        if (type === 'line') {
          item.parentId = null;
          if (parent) item.parentName = parent;
        } else if (type === 'calculation') {
          item.calculation = { operator: 'subtract', lineIds: [] };
        }

        if (type === 'header') headerMap[name] = item.id;
        newItems.push(item);
      });

      newItems.forEach(item => {
        if (item.type === 'line' && item.parentName) {
          item.parentId = headerMap[item.parentName] || null;
          delete item.parentName;
        }
      });

      updateTemplate({ ...activeTemplate, items: [...activeTemplate.items, ...newItems] });
      setShowCSVUpload(false);
      alert(`Successfully imported ${newItems.length} items`);
    } catch (error) {
      alert('Error parsing CSV');
    }
  };

  const downloadCSVTemplate = () => {
    const csv = `Type,Name,Parent Header (for line items only)\nheader,Revenue,\nline,Sales Revenue,Revenue\nline,Service Revenue,Revenue\nheader,Cost of Goods Sold,\nline,Materials,Cost of Goods Sold\ncalculation,Gross Profit,`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_format.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const headers = activeTemplate?.items.filter(i => i.type === 'header') || [];

  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-slate-800">Templates</h2>
          <button onClick={createNewTemplate} className="bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-800 flex items-center text-sm">
            <Plus className="w-4 h-4 mr-1" />
            New
          </button>
        </div>
        <div className="space-y-2">
          {templates.map(template => (
            <div key={template.id} className={`p-3 rounded-lg cursor-pointer flex justify-between items-center ${activeTemplate?.id === template.id ? 'bg-blue-50 border-2 border-blue-900' : 'bg-gray-50 hover:bg-gray-100'}`} onClick={() => setActiveTemplate(template)}>
              <div>
                <div className="font-medium text-slate-800">{template.name}</div>
                <div className="text-xs text-gray-500">{template.items.length} items</div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete?')) deleteTemplate(template.id); }} className="text-red-600 hover:text-red-800">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="col-span-2 bg-white rounded-lg shadow p-6">
        {activeTemplate ? (
          <>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
              <input type="text" value={activeTemplate.name} onChange={(e) => updateTemplate({ ...activeTemplate, name: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900" />
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              <button onClick={() => addItem('header')} className="bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-600 text-sm">+ Header</button>
              <button onClick={() => addItem('line')} className="bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 text-sm">+ Line Item</button>
              <button onClick={() => addItem('calculation')} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-500 text-sm">+ Calculation</button>
              <button onClick={() => setShowCSVUpload(!showCSVUpload)} className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-600 text-sm flex items-center">
                <Upload className="w-4 h-4 mr-1" />
                Import CSV
              </button>
            </div>

            {showCSVUpload && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-semibold mb-2 text-green-900">Import Template from CSV</h3>
                <div className="flex gap-2">
                  <label className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-600 cursor-pointer text-sm">
                    Choose CSV
                    <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
                  </label>
                  <button onClick={downloadCSVTemplate} className="bg-white text-green-700 border border-green-700 px-4 py-2 rounded-lg text-sm">Download Template</button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {activeTemplate.items.map((item, index) => (
                <div key={item.id} className={`p-4 rounded-lg border ${item.type === 'header' ? 'bg-slate-50 border-slate-300' : item.type === 'calculation' ? 'bg-gray-50 border-gray-300' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {editingItem === item.id ? (
                        <input type="text" value={item.name} onChange={(e) => updateItem(item.id, { name: e.target.value })} onBlur={() => setEditingItem(null)} className="w-full px-3 py-1 border rounded" autoFocus />
                      ) : (
                        <div onClick={() => setEditingItem(item.id)} className={`cursor-pointer ${item.type === 'header' ? 'font-bold text-lg' : ''}`}>
                          {item.name}
                          {item.type === 'calculation' && <span className="ml-2 text-xs text-gray-500">(calculation)</span>}
                          {item.type === 'line' && item.parentId && <span className="ml-2 text-xs text-blue-700">→ {headers.find(h => h.id === item.parentId)?.name}</span>}
                        </div>
                      )}
                      
                      {item.type === 'line' && (
                        <div className="mt-2">
                          <label className="text-xs text-gray-600">Maps to header:</label>
                          <select value={item.parentId || ''} onChange={(e) => updateItem(item.id, { parentId: e.target.value || null })} className="ml-2 text-sm px-2 py-1 border rounded">
                            <option value="">None</option>
                            {headers.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                          </select>
                        </div>
                      )}

                      {item.type === 'calculation' && (
                        <CalculationEditor item={item} allItems={activeTemplate.items} updateItem={updateItem} />
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button onClick={() => moveItem(item.id, 'up')} disabled={index === 0} className="text-gray-500 hover:text-gray-700 disabled:opacity-30">↑</button>
                      <button onClick={() => moveItem(item.id, 'down')} disabled={index === activeTemplate.items.length - 1} className="text-gray-500 hover:text-gray-700 disabled:opacity-30">↓</button>
                      <button onClick={() => deleteItem(item.id)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center text-gray-500 py-12">Select or create a template</div>
        )}
      </div>
    </div>
  );
};

const CalculationEditor = ({ item, allItems, updateItem }) => {
  const lineItems = allItems.filter(i => i.type === 'line' || i.type === 'header');
  
  const addOperand = () => {
    const calc = item.calculation || { operator: 'subtract', lineIds: [] };
    updateItem(item.id, { calculation: { ...calc, lineIds: [...calc.lineIds, ''] } });
  };

  const updateOperand = (index, lineId) => {
    const calc = item.calculation;
    const newLineIds = [...calc.lineIds];
    newLineIds[index] = lineId;
    updateItem(item.id, { calculation: { ...calc, lineIds: newLineIds } });
  };

  const removeOperand = (index) => {
    const calc = item.calculation;
    const newLineIds = calc.lineIds.filter((_, i) => i !== index);
    updateItem(item.id, { calculation: { ...calc, lineIds: newLineIds } });
  };

  return (
    <div className="mt-2 space-y-2">
      <select value={item.calculation.operator} onChange={(e) => updateItem(item.id, { calculation: { ...item.calculation, operator: e.target.value } })} className="text-sm px-2 py-1 border rounded">
        <option value="add">Add (+)</option>
        <option value="subtract">Subtract (-)</option>
      </select>
      
      {item.calculation.lineIds.map((lineId, index) => (
        <div key={index} className="flex items-center space-x-2">
          <select value={lineId} onChange={(e) => updateOperand(index, e.target.value)} className="flex-1 text-sm px-2 py-1 border rounded">
            <option value="">Select item...</option>
            {lineItems.map(li => <option key={li.id} value={li.id}>{li.name}</option>)}
          </select>
          <button onClick={() => removeOperand(index)} className="text-red-600 hover:text-red-800">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
      
      <button onClick={addOperand} className="text-sm text-blue-900 hover:text-blue-700">+ Add operand</button>
    </div>
  );
};

const MappingInterface = ({ activeTemplate, trialBalance, mappings, updateMapping, handleFileUpload, unmappedAccounts, generateStatements }) => {
  const lineItems = activeTemplate?.items.filter(i => i.type === 'line') || [];

  return (
    <div className="space-y-6">
      {trialBalance.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Upload className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold mb-2">Upload Trial Balance</h3>
          <p className="text-gray-600 mb-2">Upload your trial balance CSV</p>
          <p className="text-sm text-gray-500 mb-6">Accepts 2 columns (Account Name, Amount) or 3 columns (Account #, Name, Amount)</p>
          <label className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 cursor-pointer inline-block">
            Choose File
            <input type="file" accept=".csv,.xls,.xlsx" onChange={handleFileUpload} className="hidden" />
          </label>
        </div>
      ) : (
        <>
          {unmappedAccounts.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="font-medium text-yellow-800">{unmappedAccounts.length} unmapped accounts</p>
              <p className="text-sm text-yellow-700">All accounts must be mapped before generating statements</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Account #</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Account Name</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold">Amount</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Map to Line Item</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {trialBalance.map((account) => (
                  <tr key={account.accountNumber} className={!mappings[account.accountNumber] ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 text-sm font-mono">{account.accountNumber}</td>
                    <td className="px-6 py-4 text-sm">{account.accountName}</td>
                    <td className="px-6 py-4 text-sm text-right font-mono">${account.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="px-6 py-4">
                      <select value={mappings[account.accountNumber] || ''} onChange={(e) => updateMapping(account.accountNumber, e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                        <option value="">Select line item...</option>
                        {lineItems.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between">
            <label className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-500 cursor-pointer inline-flex items-center">
              <Upload className="w-4 h-4 mr-2" />
              Upload New File
              <input type="file" accept=".csv,.xls,.xlsx" onChange={handleFileUpload} className="hidden" />
            </label>
            <button onClick={generateStatements} disabled={unmappedAccounts.length > 0} className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 disabled:bg-gray-300 flex items-center">
              Generate Statements
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const StatementView = ({ activeTemplate, trialBalance, mappings, companyName, setCompanyName, periodEnding, setPeriodEnding }) => {
  const calculateLineValue = (item) => {
    if (item.type === 'line') {
      return trialBalance.filter(acc => mappings[acc.accountNumber] === item.id).reduce((sum, acc) => sum + acc.amount, 0);
    } else if (item.type === 'calculation' && item.calculation) {
      const { operator, lineIds } = item.calculation;
      let result = 0;
      lineIds.forEach((lineId, index) => {
        const lineItem = activeTemplate.items.find(i => i.id === lineId);
        if (lineItem) {
          const value = calculateLineValue(lineItem);
          if (index === 0) result = value;
          else result = operator === 'add' ? result + value : result - value;
        }
      });
      return result;
    } else if (item.type === 'header') {
      return activeTemplate.items.filter(i => i.type === 'line' && i.parentId === item.id).reduce((sum, child) => sum + calculateLineValue(child), 0);
    }
    return 0;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Enter company name" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Period Ending</label>
            <input type="text" value={periodEnding} onChange={(e) => setPeriodEnding(e.target.value)} placeholder="e.g., December 31, 2024" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900" />
          </div>
        </div>
        <button onClick={() => window.print()} className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 flex items-center">
          <Download className="w-4 h-4 mr-2" />
          Export to PDF
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">{companyName || 'Company Name'}</h1>
          <h2 className="text-xl font-semibold text-slate-700 mt-2">{activeTemplate.name}</h2>
          <p className="text-gray-600 mt-1">{periodEnding || 'Period Ending'}</p>
        </div>

        <div className="space-y-1">
          {activeTemplate.items.map((item) => {
            const value = calculateLineValue(item);
            return (
              <div key={item.id} className={`flex justify-between py-2 ${item.type === 'header' ? 'font-bold text-lg border-b-2 border-slate-800 mt-4' : item.type === 'calculation' ? 'font-semibold border-t border-gray-300 mt-2' : 'pl-4'}`}>
                <span>{item.name}</span>
                <span className="font-mono">${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FinancialApp;
